import React, { HTMLAttributes, useMemo, useState } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { dracula } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { ChatResponse } from "../api/models";
import AnswerCitationModal from "./AnswerCitationModal";
import CLOHelpCenter from "./SVGIcons/CLOHelpCenter";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { useAppContext } from "../hooks/useAppContext";

export default function Markdown({ answer }: { answer: ChatResponse }) {
  const { theme } = useAppContext();
  const isDark = theme === "dark";
  const [copied, setCopied] = useState(false);

  const handleCopy = (
    event: React.MouseEvent<HTMLButtonElement>,
    text: string,
  ) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const removeStrongTags = (node: React.ReactNode) =>
    React.Children.toArray(node).flatMap((child) => {
      if (!React.isValidElement(child)) return child;
      if (child.type === "strong" || child.type === "b") {
        // Remove the tag by returning its children directly
        return React.Children.toArray(child.props.children);
      }
      return child;
    });

  const h1: React.FC<HTMLAttributes<HTMLHeadingElement>> = ({
    children,
    ...props
  }) => {
    return (
      <h1 className="pl-0 text-lg font-bold text-blue-solid-blue6" {...props}>
        {removeStrongTags(children)}
      </h1>
    );
  };

  const h2: React.FC<HTMLAttributes<HTMLHeadingElement>> = ({
    children,
    ...props
  }) => (
    <h2 className="pl-0 text-lg font-bold text-blue-solid-blue6" {...props}>
      {removeStrongTags(children)}
    </h2>
  );

  const h3: React.FC<HTMLAttributes<HTMLHeadingElement>> = ({
    children,
    ...props
  }) => (
    <h3 className="pl-0 text-lg font-bold text-blue-solid-blue6" {...props}>
      {removeStrongTags(children)}
    </h3>
  );

  const h4: React.FC<HTMLAttributes<HTMLHeadingElement>> = ({
    children,
    ...props
  }) => (
    <h4 className="pl-0 text-lg font-bold text-blue-solid-blue6" {...props}>
      {removeStrongTags(children)}
    </h4>
  );

  const h5: React.FC<HTMLAttributes<HTMLHeadingElement>> = ({
    children,
    ...props
  }) => (
    <h5 className="pl-0 text-lg font-bold text-blue-solid-blue6" {...props}>
      {removeStrongTags(children)}
    </h5>
  );

  const h6: React.FC<HTMLAttributes<HTMLHeadingElement>> = ({
    children,
    ...props
  }) => (
    <h6 className="pl-0 text-lg font-bold text-blue-solid-blue6" {...props}>
      {removeStrongTags(children)}
    </h6>
  );

  const p: React.FC<HTMLAttributes<HTMLParagraphElement>> = ({
    children,
    ...props
  }) => {
    const textParts = React.Children.toArray(children);

    let a_elements: React.ReactElement[] = [];
    const newChildren = textParts.map((part, index) => {
      if (
        React.isValidElement(part) &&
        part.type === a &&
        !part.props.href?.includes("https://www.clo3d.com/")
      ) {
        a_elements.push(part);
        return null;
      }

      if (React.isValidElement(part) && part.type === "img") {
        return (
          <span key={index} className="mt-2 block">
            {part}
          </span>
        );
      }

      return part;
    });

    return (
      <div className="text-base leading-7 text-grayscale-solid-g9" {...props}>
        <span>{newChildren}</span>
        {a_elements}
      </div>
    );
  };

  const img: React.FC<HTMLAttributes<HTMLImageElement>> = ({
    children,
    ...props
  }) => (
    <img
      className="inline-block rounded-md bg-grayscale-solid-fixed-white"
      {...props}
    >
      {children}
    </img>
  );

  const table: React.FC<HTMLAttributes<HTMLTableElement>> = ({
    children,
    ...props
  }) => (
    <div className="my-2 w-full overflow-x-auto">
      <table
        className="w-full min-w-full border-collapse border border-grayscale-opacity-blevel3"
        {...props}
      >
        {children}
      </table>
    </div>
  );

  const thead: React.FC<HTMLAttributes<HTMLTableSectionElement>> = ({
    children,
    ...props
  }) => (
    <thead
      className={
        isDark ? "bg-grayscale-solid-g2" : "bg-grayscale-solid-g1"
      }
      {...props}
    >
      {children}
    </thead>
  );

  const th: React.FC<HTMLAttributes<HTMLTableCellElement>> = ({
    children,
    ...props
  }) => (
    <th
      className={`border border-grayscale-opacity-blevel3 px-3 py-2 text-left text-sm font-bold text-grayscale-solid-g9 ${isDark ? "bg-grayscale-solid-g2" : "bg-grayscale-solid-g1"}`}
      {...props}
    >
      {children}
    </th>
  );

  const td: React.FC<HTMLAttributes<HTMLTableCellElement>> = ({
    children,
    ...props
  }) => (
    <td
      className={`border border-grayscale-opacity-blevel3 px-3 py-2 text-left text-sm text-grayscale-solid-g9 ${isDark ? "bg-grayscale-solid-g1" : "bg-grayscale-solid-g0"}`}
      {...props}
    >
      {children}
    </td>
  );

  const tbody: React.FC<HTMLAttributes<HTMLTableSectionElement>> = ({
    children,
    ...props
  }) => (
    <tbody
      className={
        isDark ? "bg-grayscale-solid-g1" : "bg-grayscale-solid-g0"
      }
      {...props}
    >
      {children}
    </tbody>
  );

  const tr: React.FC<HTMLAttributes<HTMLTableRowElement>> = ({
    children,
    ...props
  }) => <tr {...props}>{children}</tr>;

  const ul: React.FC<HTMLAttributes<HTMLUListElement>> = ({
    children,
    ...props
  }) => (
    <ul className="flex list-disc flex-col gap-2 pl-7" {...props}>
      {children}
    </ul>
  );

  const ol: React.FC<HTMLAttributes<HTMLOListElement>> = ({
    children,
    ...props
  }) => (
    <ol className="flex list-decimal flex-col gap-2 pl-7" {...props}>
      {children}
    </ol>
  );

  const li: React.FC<HTMLAttributes<HTMLLIElement>> = ({
    children,
    ...props
  }) => {
    const textParts = React.Children.toArray(children);

    let a_elements: React.ReactElement[] = [];
    const newChildren = textParts.map((part, index) => {
      if (
        React.isValidElement(part) &&
        part.type === a &&
        !part.props.href?.includes("https://www.clo3d.com/")
      ) {
        a_elements.push(part);
        return null;
      }
      return part;
    });

    return (
      <li className="text-base text-grayscale-solid-g9" {...props}>
        <div className="flex flex-wrap items-center gap-1.5">
          <span>{newChildren}</span>
          {a_elements}
        </div>
      </li>
    );
  };

  const a: React.FC<React.PropsWithChildren<{ href?: string }>> = ({
    href,
    children,
  }) => {
    let article = answer.citation_articles.find(
      (article) => article.url === href,
    );

    let key = "";
    if (article?.title) {
      key = article.title + "_" + href + "_" + children?.toString();
    } else if (article?.url) {
      key = article.url + "_" + children?.toString();
    } else {
      key = href + "_" + children?.toString();
    }

    return (
      <span
        className="group relative mx-1 inline-block cursor-pointer"
        key={key}
        onMouseEnter={(event) => {
          let parent = event.currentTarget.parentElement?.children[0];

          if (
            parent &&
            (parent.tagName === "SPAN" || parent.tagName === "P") &&
            !href?.includes("contact") &&
            !href?.includes("https://connect.clo-set.com/community/qna") &&
            !href?.includes("https://www.clo3d.com/")
          ) {
            parent.classList.add("bg-yellow-opacity-level5");
          }
        }}
        onMouseLeave={(event) => {
          let parent = event.currentTarget.parentElement?.children[0];

          if (
            parent &&
            (parent.tagName === "SPAN" || parent.tagName === "P") &&
            !href?.includes("contact") &&
            !href?.includes("https://connect.clo-set.com/community/qna") &&
            !href?.includes("https://www.clo3d.com/")
          ) {
            parent.classList.remove("bg-yellow-opacity-level5");
          }
        }}
      >
        <a
          className="inline-block rounded-[30px] px-2 py-0.5 text-xs font-medium leading-[17px] text-grayscale-solid-g10 no-underline outline outline-1 outline-offset-[-1px] outline-grayscale-opacity-blevel3 group-hover:bg-grayscale-solid-g3 group-hover:!text-grayscale-solid-g10 group-hover:text-white sm:text-sm"
          href={href}
          target="_blank"
        >
          {children}
        </a>
        {article?.content_description && (
          <AnswerCitationModal
            className={`absolute left-0 top-[20px] hidden group-hover:block`}
            logo={<CLOHelpCenter className="h-7 w-7" />}
            title={article?.title || children?.toString() || ""}
            url={href || ""}
            contentDescription={article?.content_description}
            createdAt={article?.created_at?.split("T")[0]}
          />
        )}
      </span>
    );
  };

  const processedContent = useMemo(() => {
    // This will find links and track duplicates
    const linkCount: Record<string, number> = {};

    // Replace markdown links: [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

    return (
      answer.answer
        .replace(linkRegex, (match, text, url) => {
          const key = `${text}`;

          if (linkCount[key]) {
            linkCount[key] += 1;
            return `[${text} (${linkCount[key]})](${url})`;
          } else {
            linkCount[key] = 1;
            return match; // first occurrence stays normal
          }
        })
        // Handles legacy links that do not have a format: [text](url)
        .replace(/\[https?:\/\/([^\]]+)\]/g, (match, url) => {
          const title = url.split("/")[0];
          return `[${title}](https://${url})`;
        })
        .replace(/<a\s+href="([^"]+).*">([^<]+)<\/a>/g, "[$2]($1)")
    );
  }, [answer.answer]);

  return (
    <div className="flex w-full flex-col gap-3">
      <ReactMarkdown
        urlTransform={(url) =>
          url.startsWith("data:") ? url : defaultUrlTransform(url)
        }
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          h1: h1,
          h2: h2,
          h3: h3,
          h4: h4,
          h5: h5,
          h6: h6,
          p: p,
          img: img,
          table: table,
          thead: thead,
          th: th,
          td: td,
          tbody: tbody,
          tr: tr,
          li: li,
          ul: ul,
          ol: ol,
          a: a,
          code({ className, children, ...rest }) {
            const match = /language-(\w+)/.exec(className || "") || "bash";
            return (
              <div className="relative my-2 w-full min-w-0 overflow-hidden rounded-md">
                <button
                  onClick={(event) =>
                    handleCopy(event, children?.toString() || "")
                  }
                  className="absolute right-0 top-0 z-10 rounded-tr-md bg-gray-700/50 px-2 py-1 text-xs text-white backdrop-blur-md transition hover:bg-gray-600"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>

                <SyntaxHighlighter
                  PreTag="div"
                  customStyle={{
                    margin: 0,
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: "auto", // Enables scrolling
                    whiteSpace: "pre-wrap", // Forces code to wrap if it's too long
                    wordBreak: "break-all", // Ensures long strings break
                  }}
                  codeTagProps={{
                    style: {
                      paddingRight: "3rem",
                      whiteSpace: "pre-wrap",
                    },
                  }}
                  language={match[1]}
                  showLineNumbers
                  style={dracula}
                  wrapLines={true}
                  {...Object.fromEntries(
                    Object.entries(rest).filter(([key]) => key !== "ref"),
                  )}
                >
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
              </div>
            );
          },
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}
